// Stadium-Trial-only i18n. The rest of the app stays English even when the global
// language is "fr" (see I18nContext: fr maps to en). This dictionary is the ONLY
// place French is applied. Read via the useST() hook.
//
// Values are strings or (…args) => string for interpolation. Missing keys fall
// back to English.

import { useI18n } from "../../i18n/I18nContext";
import type { Language } from "../../i18n/I18nContext";

type STVal = string | ((...args: any[]) => string);
type STDict = Record<string, STVal>;

const EN: STDict = {
  // ── Pre-run ──
  allTiers: "All tiers",
  runPath: "Run Path · 10 Stages",
  health: "Health",
  hpValue: (hp: number, max: number) => `${hp} / ${max} HP`,
  fullyRested: "✦ Fully rested",
  fullIn: (t: string) => `↻ Full in ${t}`,
  runRewards: "Run Rewards",
  fullClear: "full clear · all 10 stages",
  labelXp: "XP",
  labelGold: "Gold",
  labelPack: "Pack",
  noConditionRegen: (t: string) => `No Condition — full regen in ${t}.`,
  loading: "Loading…",
  resume: (stage: number, total: number) => `▶️ Resume — Stage ${stage}/${total}`,
  startRun: "⚔️ Start Run",
  unavailable: "Unavailable",

  // ── All tiers modal ──
  allTiersTitle: "All Tiers",
  tierN: (n: number) => `Tier ${n}`,
  recommended: "Recommended:",
  bossStage5: "Boss · Stage 5",
  finalStage10: "Final · Stage 10",
  statusCleared: "✓ Cleared",
  statusCurrent: "▶ Current",
  statusLocked: "🔒 Locked",
  moreTiers: "More tiers coming soon.",

  // ── Camp ──
  camp: "🏕️ Camp",
  yourStats: "Your Stats",
  statPower: "Power",
  statTechnique: "Technique",
  statLife: "Life",
  statDefense: "Defense",
  hintDmgHit: "dmg / hit",
  hintCrit: "crit ×2",
  hintMaxHp: "max HP",
  hintDmgUp: "+20% dmg",
  hintTaken: (p: number) => `-${p}% taken`,
  activeBoosts: "Active Boosts",
  chooseBoost: "Choose a Boost",
  slotN: (n: number, max: number) => `Slot ${n} / ${max}`,
  slotsFull: "Your 3 slots are full — pick a new boost, then one to replace.",
  replaceTitle: "Replace a Boost",
  newBoost: "New boost",
  replaceWhich: "Replace which",
  replaceBoost: "Replace Boost",
  close: "Close",
  confirmBoost: "✓ Confirm Boost",
  selectBoost: "Select a boost",
  startStage: (n: number) => `⚔️ Start Stage ${n}`,
  noCondition: "❤️ No Condition",
  recoverFirst: "Recover condition before continuing.",
  nextRoom: (n: number) => `Next Room · Stage ${n}`,
  nextBoss: "Next Boss",
  nextMinion: "Next Minion",
  checkStats: "Check Stats",
  enemyStatsTitle: "Enemy Stats",
  statHpMax: "Max HP",
  statDamage: "Damage",
  hintDmgOut: "outgoing dmg",
  allClear: "All clear ahead",
  tagMob: "MOB",
  tagBoss: "BOSS",
  tagFinal: "FINAL",
  noActiveBoosts: "No active boosts yet",

  // ── Combat ──
  stageBoss: (n: number) => `Stage ${n} Boss`,
  you: "You",
  minion: "Minion",
  crit: "CRIT!",
  skip: "SKIP ⏭",

  // ── Boss result ──
  success: "Success",
  defeat: "Defeat",
  subCleared: "Stadium cleared — all 10 stages!",
  subStageCleared: (n: number) => `Stage ${n} cleared`,
  subReached: (n: number) => `Reached Stage ${n}`,
  damageTaken: "Damage taken",
  stageRewards: "Stage Rewards",
  chestEarned: "Bonus Chest earned!",
  chestReason: "Reward for clearing the final boss.",
  runTotal: "Run Total",
  claimRewards: "🏆 Claim Rewards",
  endRun: "💀 End Run",
  continueArrow: "Continue →",
  savingStage: "Saving stage…",
  retry: "↻ Retry",

  // ── Run result ──
  stadiumCleared: "Stadium Cleared!",
  reachedStage: (n: number) => `Reached Stage ${n}`,
  newBest: "★ New personal best!",
  rewardsEarned: "Rewards Earned",
  savingRewards: "Saving rewards…",
  rewardsSaved: "✓ Rewards saved",
  retrySave: "↻ Retry save",
  backToHub: "← Back to League Hub",

  // ── Spells (A/B variant B) ──
  chooseSpells: "Choose Your Spells",
  spellsForRun: (n: number) => `Pick ${n} spells — locked in for the whole run`,
  spellsSelected: (n: number, max: number) => `${n} / ${max} selected`,
  unlockAtLv: (n: number) => `Unlocks at Lv ${n}`,
  confirmSpells: "✓ Lock In Spells",
  selectMoreSpells: (n: number) => `Select ${n} more`,
  yourSpells: "Your Spells",
  viewSpells: "📖 Spells",
  alwaysEquipped: "Always equipped",
  energyLabel: "Mana",
  perTurn: "per turn",
  endTurn: "End Turn ✓",
  roundN: (n: number) => `Round ${n}`,
  enemyTurn: "Enemy turn…",
  critRefund: "CRIT +1 💧",
  spellCost: (n: number) => `${n} 💧`,
  yourTurn: "Your turn — spend your mana",
  buffFocus: (p: number) => `🧠 +${p}% this turn`,
  oncePerTurnUsed: "Used",
  buffOffBalance: (p: number) => `💫 Off Balance — next hit +${p}%`,
  cooldownShort: (n: number) => `⏳ ${n}`,
  hintSpellDmg: (n: number) => `≈${n} dmg`,
  hintSpellDmgPerEnergy: (n: number) => `+${n} per enemy 💧`,
  hintSpellSelfDmg: (n: number) => `-${n} HP`,
  hintSpellHealCrit: (n: number) => `crit +${n} HP`,
  hintSpellShield: (n: number) => `🛡 ${n}`,
  enemyEnergyLabel: "Enemy",
  buffEnemyRage: (p: number) => `🔥 Enemy rage +${p}%`,
  // ── 2v2 pets test ──
  petTurn: "Companion turn — spend its energy",
  petLabel: "Companion",
  petDefaultName: "Companion",
  buffBattleCry: (p: number) => `📣 Battle Cry — master's next attack +${p}%`,
  companionSpells: "Companion Spells",
};

const FR: STDict = {
  // ── Pré-run ──
  allTiers: "Tous les paliers",
  runPath: "Parcours · 10 salles",
  health: "Vie",
  hpValue: (hp: number, max: number) => `${hp} / ${max} PV`,
  fullyRested: "✦ Pleine forme",
  fullIn: (t: string) => `↻ Plein dans ${t}`,
  runRewards: "Récompenses de run",
  fullClear: "run complète · 10 salles",
  labelXp: "XP",
  labelGold: "Or",
  labelPack: "Pack",
  noConditionRegen: (t: string) => `Plus de vie — régén complète dans ${t}.`,
  loading: "Chargement…",
  resume: (stage: number, total: number) => `▶️ Reprendre — Salle ${stage}/${total}`,
  startRun: "⚔️ Lancer la run",
  unavailable: "Indisponible",

  // ── Modale paliers ──
  allTiersTitle: "Tous les paliers",
  tierN: (n: number) => `Palier ${n}`,
  recommended: "Recommandé :",
  bossStage5: "Boss · Salle 5",
  finalStage10: "Final · Salle 10",
  statusCleared: "✓ Terminé",
  statusCurrent: "▶ En cours",
  statusLocked: "🔒 Verrouillé",
  moreTiers: "Plus de paliers bientôt.",

  // ── Camp ──
  camp: "🏕️ Camp",
  yourStats: "Tes stats",
  statPower: "Puissance",
  statTechnique: "Technique",
  statLife: "Vie",
  statDefense: "Défense",
  hintDmgHit: "dégât / coup",
  hintCrit: "crit ×2",
  hintMaxHp: "PV max",
  hintDmgUp: "+20% dégâts",
  hintTaken: (p: number) => `-${p}% subis`,
  activeBoosts: "Boosts actifs",
  chooseBoost: "Choisis un boost",
  slotN: (n: number, max: number) => `Slot ${n} / ${max}`,
  slotsFull: "Tes 3 slots sont pleins — choisis un nouveau boost, puis un à remplacer.",
  replaceTitle: "Remplacer un boost",
  newBoost: "Nouveau boost",
  replaceWhich: "Lequel remplacer",
  replaceBoost: "Remplacer",
  close: "Fermer",
  confirmBoost: "✓ Valider le boost",
  selectBoost: "Sélectionne un boost",
  startStage: (n: number) => `⚔️ Lancer la salle ${n}`,
  noCondition: "❤️ Plus de vie",
  recoverFirst: "Récupère de la vie avant de continuer.",
  nextRoom: (n: number) => `Prochaine salle · Salle ${n}`,
  nextBoss: "Prochain boss",
  nextMinion: "Prochain sbire",
  checkStats: "Voir les stats",
  enemyStatsTitle: "Stats de l'ennemi",
  statHpMax: "PV max",
  statDamage: "Dégâts",
  hintDmgOut: "dégâts sortants",
  allClear: "Plus rien à venir",
  tagMob: "SBIRE",
  tagBoss: "BOSS",
  tagFinal: "FINAL",
  noActiveBoosts: "Aucun boost actif",

  // ── Combat ──
  stageBoss: (n: number) => `Boss salle ${n}`,
  you: "Toi",
  minion: "Sbire",
  crit: "CRIT !",
  skip: "PASSER ⏭",

  // ── Résultat de combat ──
  success: "Victoire",
  defeat: "Défaite",
  subCleared: "Stade terminé — les 10 salles !",
  subStageCleared: (n: number) => `Salle ${n} terminée`,
  subReached: (n: number) => `Salle ${n} atteinte`,
  damageTaken: "Dégâts subis",
  stageRewards: "Récompenses de salle",
  chestEarned: "Coffre bonus gagné !",
  chestReason: "Récompense pour avoir battu le boss final.",
  runTotal: "Total de la run",
  claimRewards: "🏆 Récupérer",
  endRun: "💀 Fin de run",
  continueArrow: "Continuer →",
  savingStage: "Sauvegarde…",
  retry: "↻ Réessayer",

  // ── Résultat de run ──
  stadiumCleared: "Stade terminé !",
  reachedStage: (n: number) => `Salle ${n} atteinte`,
  newBest: "★ Nouveau record !",
  rewardsEarned: "Récompenses gagnées",
  savingRewards: "Sauvegarde des récompenses…",
  rewardsSaved: "✓ Récompenses sauvegardées",
  retrySave: "↻ Réessayer",
  backToHub: "← Retour au hub",

  // ── Sorts (variante A/B) ──
  chooseSpells: "Choisis tes sorts",
  spellsForRun: (n: number) => `Choisis ${n} sorts — verrouillés pour toute la run`,
  spellsSelected: (n: number, max: number) => `${n} / ${max} sélectionnés`,
  unlockAtLv: (n: number) => `Débloqué au niveau ${n}`,
  confirmSpells: "✓ Verrouiller les sorts",
  selectMoreSpells: (n: number) => `Choisis-en encore ${n}`,
  yourSpells: "Tes sorts",
  viewSpells: "📖 Sorts",
  alwaysEquipped: "Toujours équipée",
  energyLabel: "Mana",
  perTurn: "par tour",
  endTurn: "Fin de tour ✓",
  roundN: (n: number) => `Tour ${n}`,
  enemyTurn: "Tour ennemi…",
  critRefund: "CRIT +1 💧",
  spellCost: (n: number) => `${n} 💧`,
  yourTurn: "À toi — dépense ton mana",
  buffFocus: (p: number) => `🧠 +${p}% ce tour`,
  oncePerTurnUsed: "Utilisé",
  buffOffBalance: (p: number) => `💫 Déséquilibré — prochain coup +${p}%`,
  cooldownShort: (n: number) => `⏳ ${n}`,
  hintSpellDmg: (n: number) => `≈${n} dégâts`,
  hintSpellDmgPerEnergy: (n: number) => `+${n}/💧 ennemie`,
  hintSpellSelfDmg: (n: number) => `-${n} PV`,
  hintSpellHealCrit: (n: number) => `crit +${n} PV`,
  hintSpellShield: (n: number) => `🛡 ${n}`,
  enemyEnergyLabel: "Ennemi",
  buffEnemyRage: (p: number) => `🔥 Rage ennemie +${p}%`,
  // ── Test familiers 2v2 ──
  petTurn: "Tour du familier — dépense son énergie",
  petLabel: "Familier",
  petDefaultName: "Familier",
  buffBattleCry: (p: number) => `📣 Cri de guerre — prochaine attaque du maître +${p}%`,
  companionSpells: "Sorts du familier",
};

// Korean (Stadium Trial). Typed as a total map over EN's keys so TypeScript
// FORCES completeness — a missing key here is a compile error, not a silent
// English fallback. Vocabulary matches the main ko.ts (파워/기술/방어/생명력/체력/골드/팩).
const KO: Record<keyof typeof EN, STVal> = {
  // ── Pre-run ──
  allTiers: "전체 티어",
  runPath: "진행 경로 · 10 스테이지",
  health: "체력",
  hpValue: (hp: number, max: number) => `${hp} / ${max} HP`,
  fullyRested: "✦ 완전 회복",
  fullIn: (t: string) => `↻ ${t} 후 완전 회복`,
  runRewards: "런 보상",
  fullClear: "전체 클리어 · 10 스테이지 전부",
  labelXp: "XP",
  labelGold: "골드",
  labelPack: "팩",
  noConditionRegen: (t: string) => `컨디션 없음 — ${t} 후 완전 회복.`,
  loading: "로딩 중…",
  resume: (stage: number, total: number) => `▶️ 이어하기 — 스테이지 ${stage}/${total}`,
  startRun: "⚔️ 런 시작",
  unavailable: "이용 불가",

  // ── All tiers modal ──
  allTiersTitle: "전체 티어",
  tierN: (n: number) => `티어 ${n}`,
  recommended: "권장:",
  bossStage5: "보스 · 스테이지 5",
  finalStage10: "최종 · 스테이지 10",
  statusCleared: "✓ 클리어",
  statusCurrent: "▶ 진행 중",
  statusLocked: "🔒 잠김",
  moreTiers: "더 많은 티어가 곧 추가됩니다.",

  // ── Camp ──
  camp: "🏕️ 캠프",
  yourStats: "내 스탯",
  statPower: "파워",
  statTechnique: "기술",
  statLife: "생명력",
  statDefense: "방어",
  hintDmgHit: "타격당 데미지",
  hintCrit: "치명타 ×2",
  hintMaxHp: "최대 체력",
  hintDmgUp: "+20% 데미지",
  hintTaken: (p: number) => `-${p}% 피해`,
  activeBoosts: "활성 부스트",
  chooseBoost: "부스트 선택",
  slotN: (n: number, max: number) => `슬롯 ${n} / ${max}`,
  slotsFull: "슬롯 3개가 가득 찼습니다 — 새 부스트를 고른 뒤 교체할 부스트를 선택하세요.",
  replaceTitle: "부스트 교체",
  newBoost: "새 부스트",
  replaceWhich: "교체할 부스트",
  replaceBoost: "부스트 교체",
  close: "닫기",
  confirmBoost: "✓ 부스트 확정",
  selectBoost: "부스트를 선택하세요",
  startStage: (n: number) => `⚔️ 스테이지 ${n} 시작`,
  noCondition: "❤️ 컨디션 없음",
  recoverFirst: "계속하기 전에 컨디션을 회복하세요.",
  nextRoom: (n: number) => `다음 방 · 스테이지 ${n}`,
  nextBoss: "다음 보스",
  nextMinion: "다음 미니언",
  checkStats: "스탯 확인",
  enemyStatsTitle: "적 스탯",
  statHpMax: "최대 체력",
  statDamage: "데미지",
  hintDmgOut: "가하는 데미지",
  allClear: "남은 적 없음",
  tagMob: "몹",
  tagBoss: "보스",
  tagFinal: "최종",
  noActiveBoosts: "활성 부스트 없음",

  // ── Combat ──
  stageBoss: (n: number) => `스테이지 ${n} 보스`,
  you: "나",
  minion: "미니언",
  crit: "치명타!",
  skip: "건너뛰기 ⏭",

  // ── Boss result ──
  success: "성공",
  defeat: "패배",
  subCleared: "스타디움 클리어 — 10 스테이지 전부!",
  subStageCleared: (n: number) => `스테이지 ${n} 클리어`,
  subReached: (n: number) => `스테이지 ${n} 도달`,
  damageTaken: "받은 피해",
  stageRewards: "스테이지 보상",
  chestEarned: "보너스 상자 획득!",
  chestReason: "최종 보스 클리어 보상.",
  runTotal: "런 합계",
  claimRewards: "🏆 보상 받기",
  endRun: "💀 런 종료",
  continueArrow: "계속 →",
  savingStage: "스테이지 저장 중…",
  retry: "↻ 다시 시도",

  // ── Run result ──
  stadiumCleared: "스타디움 클리어!",
  reachedStage: (n: number) => `스테이지 ${n} 도달`,
  newBest: "★ 개인 최고 기록!",
  rewardsEarned: "획득한 보상",
  savingRewards: "보상 저장 중…",
  rewardsSaved: "✓ 보상 저장됨",
  retrySave: "↻ 저장 다시 시도",
  backToHub: "← 리그 허브로",

  // ── Spells (A/B variant B) ──
  chooseSpells: "스펠 선택",
  spellsForRun: (n: number) => `스펠 ${n}개 선택 — 런 전체 동안 고정됩니다`,
  spellsSelected: (n: number, max: number) => `${n} / ${max} 선택됨`,
  unlockAtLv: (n: number) => `레벨 ${n}에 해금`,
  confirmSpells: "✓ 스펠 확정",
  selectMoreSpells: (n: number) => `${n}개 더 선택하세요`,
  yourSpells: "내 스펠",
  viewSpells: "📖 스펠",
  alwaysEquipped: "항상 장착됨",
  energyLabel: "마나",
  perTurn: "턴당",
  endTurn: "턴 종료 ✓",
  roundN: (n: number) => `${n}턴`,
  enemyTurn: "적의 턴…",
  critRefund: "치명타 +1 💧",
  spellCost: (n: number) => `${n} 💧`,
  yourTurn: "내 턴 — 마나를 사용하세요",
  buffFocus: (p: number) => `🧠 이 턴 +${p}%`,
  oncePerTurnUsed: "사용됨",
  buffOffBalance: (p: number) => `💫 균형 상실 — 다음 타격 +${p}%`,
  cooldownShort: (n: number) => `⏳ ${n}`,
  hintSpellDmg: (n: number) => `≈${n} 데미지`,
  hintSpellDmgPerEnergy: (n: number) => `+${n}/적 💧`,
  hintSpellSelfDmg: (n: number) => `-${n} HP`,
  hintSpellHealCrit: (n: number) => `치명타 +${n} HP`,
  hintSpellShield: (n: number) => `🛡 ${n}`,
  enemyEnergyLabel: "적",
  buffEnemyRage: (p: number) => `🔥 적 분노 +${p}%`,
  // ── 2v2 동료 테스트 ──
  petTurn: "동료의 턴 — 에너지를 사용하세요",
  petLabel: "동료",
  petDefaultName: "동료",
  buffBattleCry: (p: number) => `📣 전투의 함성 — 주인의 다음 공격 +${p}%`,
  companionSpells: "동료 스킬",
};

export type STKey = keyof typeof EN;

/** Scoped translator for Stadium Trial. Diverges from English for "fr" and "ko". */
export function useST(): { st: (key: STKey, ...args: any[]) => string; lang: Language } {
  const { language } = useI18n();
  const dict = language === "ko" ? KO : language === "fr" ? FR : EN;
  const st = (key: STKey, ...args: any[]): string => {
    const v = (dict[key] ?? EN[key]) as STVal | undefined;
    if (typeof v === "function") return v(...args);
    if (typeof v === "string") return v;
    return String(key);
  };
  return { st, lang: language };
}

/** Non-hook variant when only the language is available (e.g. helpers). */
export function stFor(language: string, key: STKey, ...args: any[]): string {
  const dict = language === "ko" ? KO : language === "fr" ? FR : EN;
  const v = (dict[key] ?? EN[key]) as STVal | undefined;
  if (typeof v === "function") return v(...args);
  if (typeof v === "string") return v;
  return String(key);
}
